import { exportSalesSummaryPdf } from "../services/salesSummaryApi";
import { useState, useEffect, type ReactNode } from "react";
import { format, startOfWeek, startOfMonth, startOfYear } from "date-fns";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useSalesSummary, useSalesSummaryAccounting, useBusinessUnitSalesSection, usePosStock } from "../hooks/useSalesSummary";
import { useAuthStore } from "@/features/auth/stores/authStore";
import type {
  SalesRepDashboard,
  SalesManagerDashboard,
  GmDashboard,
  PosDashboard,
  KpiDelta,
  TopCustomerRow,
  OrderRow,
  RegionalSalesReport,
  SalesSummaryFilters,
  FinancialDashboardAccounting,
  BusinessUnitSalesDashboard,
} from "../services/salesSummaryApi";
import { TrendingUp, TrendingDown, Minus, Download, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { normalizeError } from "@/shared/errors/normalizeError";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { getBusinessDateToday } from "@/lib/businessDate";

// ── Scope helpers ───────────────────────────────────────────────

type Scope = "today" | "week" | "month" | "ytd" | "custom" | "specific";

function scopeDates(
  scope: Scope,
  specificDate?: string,
  customRange?: { from?: string; to?: string },
): { from?: string; to?: string } {
  const today = getBusinessDateToday("Africa/Nairobi");
  const now = new Date(`${today}T12:00:00`);
  if (scope === "today") return { from: format(now, "yyyy-MM-dd"), to: format(now, "yyyy-MM-dd") };
  if (scope === "week") return { from: format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"), to: format(now, "yyyy-MM-dd") };
  if (scope === "month") return { from: format(startOfMonth(now), "yyyy-MM-dd"), to: format(now, "yyyy-MM-dd") };
  if (scope === "ytd") return { from: format(startOfYear(now), "yyyy-MM-dd"), to: format(now, "yyyy-MM-dd") };
  if (scope === "specific" && specificDate) return { from: specificDate, to: specificDate };
  if (scope === "custom" && customRange?.from) return { from: customRange.from, to: customRange.to ?? customRange.from };
  return {};
}

// ── Shared components ───────────────────────────────────────────

const CHART_COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1"];

function DeltaIndicator({ delta }: { delta: KpiDelta }) {
  const pct = delta.previous > 0 ? ((delta.change / delta.previous) * 100).toFixed(1) : delta.change > 0 ? "∞" : "0";
  if (delta.change > 0) return <span className="flex items-center text-green-600 text-xs"><TrendingUp className="h-3 w-3 mr-0.5" />+{pct}%</span>;
  if (delta.change < 0) return <span className="flex items-center text-red-600 text-xs"><TrendingDown className="h-3 w-3 mr-0.5" />{pct}%</span>;
  return <span className="flex items-center text-muted-foreground text-xs"><Minus className="h-3 w-3 mr-0.5" />0%</span>;
}

function KpiCard({ title, value, delta, format: fmt }: { title: string; value: number | string; delta?: KpiDelta; format?: "currency" | "number" | "percent" }) {
  const display = fmt === "currency"
    ? `KES ${Number(value).toLocaleString()}`
    : fmt === "percent"
      ? `${value}%`
      : Number(value).toLocaleString();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs">{title}</CardDescription>
        <CardTitle className="text-2xl">{display}</CardTitle>
      </CardHeader>
      {delta && (
        <CardContent className="pt-0">
          <DeltaIndicator delta={delta} />
        </CardContent>
      )}
    </Card>
  );
}

function AccountingExceptions({ accounting }: { accounting?: FinancialDashboardAccounting }) {
  if (!accounting) return null;
  const hasRevenueReconciliationException =
    accounting.legacyOrUnclassifiedRevenue !== 0 || accounting.revenueVariance !== 0;
  const hasArReconciliationException = accounting.accountsReceivableVariance !== null
    && accounting.accountsReceivableVariance !== 0;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Accounting reconciliation details</CardTitle>
        <CardDescription>
          Operational amounts are outside posted A/R. Unclassified revenue is retained in the posted GL total and is never guessed from descriptions.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
        <div><span className="text-muted-foreground">Posted invoice outstanding</span><div className="font-semibold">KES {accounting.postedInvoiceOutstanding.toLocaleString()}</div></div>
        <div><span className="text-muted-foreground">GL A/R vs invoice variance</span><div className={hasArReconciliationException ? "font-semibold text-amber-700" : "font-semibold"}>{accounting.accountsReceivableVariance === null ? "Unavailable" : `KES ${accounting.accountsReceivableVariance.toLocaleString()}`}</div></div>
        <div><span className="text-muted-foreground">Unposted invoices</span><div className="font-semibold">KES {accounting.operationalUnpostedInvoiceOutstanding.toLocaleString()}</div></div>
        <div><span className="text-muted-foreground">Uninvoiced orders</span><div className="font-semibold">KES {accounting.uninvoicedOrderBalances.toLocaleString()}</div></div>
        <div><span className="text-muted-foreground">Payments awaiting approval ({accounting.pendingPaymentApprovalCount})</span><div className="font-semibold">KES {accounting.pendingPaymentApprovalAmount.toLocaleString()}</div></div>
        <div><span className="text-muted-foreground">Legacy / unclassified revenue</span><div className="font-semibold">KES {accounting.legacyOrUnclassifiedRevenue.toLocaleString()}</div></div>
        <div><span className="text-muted-foreground">Revenue reconciliation variance</span><div className={hasRevenueReconciliationException ? "font-semibold text-amber-700" : "font-semibold"}>KES {accounting.revenueVariance.toLocaleString()}</div></div>
      </CardContent>
    </Card>
  );
}

function BusinessUnitAccountingOverview({ accounting }: { accounting: FinancialDashboardAccounting }) {
  return (
    <Card>
      <CardHeader><CardTitle>Accounting overview · All Sales</CardTitle><CardDescription>Existing posted-journal revenue definition. This is not filtered by business unit or region.</CardDescription></CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">Recognized revenue</p>
        <p className="text-2xl font-semibold">
          {accounting.recognizedRevenue === null
            ? "Unavailable"
            : `KES ${accounting.recognizedRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </p>
      </CardContent>
    </Card>
  );
}

function IndependentAccountingSection({ accounting, loading, failed, retry }: {
  accounting?: FinancialDashboardAccounting;
  loading: boolean;
  failed: boolean;
  retry: () => void;
}) {
  if (accounting) return <BusinessUnitAccountingOverview accounting={accounting} />;
  if (loading) return <Skeleton className="h-40" />;
  if (failed) return (
    <Card><CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
      Accounting overview unavailable.
      <Button variant="outline" size="sm" onClick={retry}>Retry accounting</Button>
    </CardContent></Card>
  );
  return null;
}

function ScopeSelector({
  scope,
  onChange,
  showYtd,
  specificDate,
}: {
  scope: Scope;
  onChange: (s: Scope) => void;
  showYtd?: boolean;
  specificDate?: string;
}) {
  const specificLabel = specificDate ? `Specific Date: ${specificDate}` : "Specific Date";

  return (
    <Select value={scope} onValueChange={(v) => onChange(v as Scope)}>
      <SelectTrigger className="w-full sm:w-[160px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="today">Today</SelectItem>
        <SelectItem value="week">This Week</SelectItem>
        <SelectItem value="month">This Month</SelectItem>
        <SelectItem value="custom">Custom Range</SelectItem>
        <SelectItem value="specific">{specificLabel}</SelectItem>
        {showYtd && <SelectItem value="ytd">Year to Date</SelectItem>}
      </SelectContent>
    </Select>
  );
}

function TopCustomersTable({ customers, onCustomerClick }: { customers: TopCustomerRow[]; onCustomerClick?: (id: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Customer</TableHead>
          <TableHead className="text-right">Orders</TableHead>
          <TableHead className="text-right">Total Spend</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {customers.map((c) => (
          <TableRow key={c.customerId} className={onCustomerClick ? "cursor-pointer" : ""} onClick={() => onCustomerClick?.(c.customerId)}>
            <TableCell className="font-medium">{c.customerName}</TableCell>
            <TableCell className="text-right">{c.orderCount}</TableCell>
            <TableCell className="text-right">KES {c.totalSpend.toLocaleString()}</TableCell>
          </TableRow>
        ))}
        {customers.length === 0 && (
          <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No data</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function OrdersTable({ orders, onOrderClick }: { orders: OrderRow[]; onOrderClick?: (id: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Order #</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead>Fulfillment</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((o) => (
          <TableRow key={o.id} className={onOrderClick ? "cursor-pointer" : ""} onClick={() => onOrderClick?.(o.id)}>
            <TableCell className="font-medium">{o.orderNumber}</TableCell>
            <TableCell>{format(new Date(o.orderDate), "dd MMM yyyy")}</TableCell>
            <TableCell>{o.customerName}</TableCell>
            <TableCell className="text-right">KES {o.totalAmount.toLocaleString()}</TableCell>
            <TableCell><Badge variant={o.paymentStatus === "Paid" ? "default" : o.paymentStatus === "Overdue" ? "destructive" : "secondary"}>{o.paymentStatus}</Badge></TableCell>
            <TableCell><Badge variant={o.fulfillmentStatus === "FullyFulfilled" ? "default" : "secondary"}>{o.fulfillmentStatus}</Badge></TableCell>
          </TableRow>
        ))}
        {orders.length === 0 && (
          <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No orders</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// ── Sales Rep View ──────────────────────────────────────────────

function SalesRepView({ data }: { data: SalesRepDashboard }) {
  const navigate = useNavigate();
  const { kpis } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard title="Orders Created" value={kpis.ordersCreated.current} delta={kpis.ordersCreated} />
        <KpiCard title={kpis.accounting ? "Recognized Revenue (Posted GL)" : "Invoiced Revenue"} value={kpis.accounting?.recognizedRevenue ?? kpis.totalRevenue.current} delta={kpis.totalRevenue} format="currency" />
        <KpiCard title="Order / POS Value (Not Sales)" value={kpis.grossSales} format="currency" />
        <KpiCard title={kpis.accounting ? "Cash Received (Posted GL)" : "Cash Collected"} value={kpis.accounting?.postedCashReceived ?? kpis.cashCollected} format="currency" />
        <KpiCard title={kpis.accounting ? "Posted A/R Control (GL)" : "Outstanding Receivables"} value={kpis.accounting?.postedAccountsReceivable ?? kpis.outstandingReceivables} format="currency" />
        <KpiCard title="Avg Order Value" value={kpis.averageOrderValue} format="currency" />
        <KpiCard title="Fully Fulfilled" value={kpis.fullyFulfilled} />
        <KpiCard title="Partially Fulfilled" value={kpis.partiallyFulfilled} />
        <KpiCard title="Awaiting Fulfillment" value={kpis.awaitingFulfillment} />
      </div>

      <AccountingExceptions accounting={kpis.accounting} />

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Top Customers</CardTitle></CardHeader>
          <CardContent>
            <TopCustomersTable customers={data.topCustomers} onCustomerClick={(id) => navigate(`/customers/${id}`)} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>My Orders</CardTitle></CardHeader>
          <CardContent>
            <OrdersTable orders={data.myOrders} onOrderClick={(id) => navigate(`/orders/${id}`)} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Sales Manager View ──────────────────────────────────────────

function SalesManagerView({ data }: { data: SalesManagerDashboard }) {
  const navigate = useNavigate();
  const { kpis } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KpiCard title={kpis.accounting ? "Recognized Revenue (Posted GL)" : "Invoiced Revenue"} value={kpis.accounting?.recognizedRevenue ?? kpis.totalRevenue.current} delta={kpis.totalRevenue} format="currency" />
        <KpiCard title="Order / POS Value (Not Sales)" value={kpis.grossSales} format="currency" />
        <KpiCard title={kpis.accounting ? "Cash Received (Posted GL)" : "Cash Collected"} value={kpis.accounting?.postedCashReceived ?? kpis.cashCollected} format="currency" />
        <KpiCard title={kpis.accounting ? "Posted A/R Control (GL)" : "Outstanding Receivables"} value={kpis.accounting?.postedAccountsReceivable ?? kpis.outstandingReceivables} format="currency" />
        <KpiCard title="Orders" value={kpis.ordersCount.current} delta={kpis.ordersCount} />
        <KpiCard title="Pending Fulfillments" value={kpis.pendingFulfillments} />
      </div>

      <AccountingExceptions accounting={kpis.accounting} />

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardDescription>Walk-in order value</CardDescription><CardTitle className="text-lg">{kpis.walkInOrdersCount} orders · KES {kpis.walkInRevenue.toLocaleString()}</CardTitle></CardHeader>
        </Card>
        <Card>
          <CardHeader><CardDescription>Scheduled order value</CardDescription><CardTitle className="text-lg">{kpis.scheduledOrdersCount} orders · KES {kpis.scheduledRevenue.toLocaleString()}</CardTitle></CardHeader>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Sales Rep Leaderboard</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Order Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.salesRepLeaderboard.map((rep, i) => (
                  <TableRow key={rep.userId}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{rep.userName}</TableCell>
                    <TableCell className="text-right">{rep.orderCount}</TableCell>
                    <TableCell className="text-right">KES {rep.revenue.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Top Customers</CardTitle></CardHeader>
          <CardContent>
            <TopCustomersTable customers={data.topCustomers} onCustomerClick={(id) => navigate(`/customers/${id}`)} />
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Top Varieties</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crop / Variety</TableHead>
                  <TableHead className="text-right">Order Value</TableHead>
                  <TableHead className="text-right">Trays</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.topVarieties.map((v, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{v.cropType}{v.variety ? ` — ${v.variety}` : ""}</TableCell>
                    <TableCell className="text-right">KES {v.revenue.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{v.trayCount.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Tray Type Breakdown</CardTitle></CardHeader>
          <CardContent>
            {data.trayTypeBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={data.trayTypeBreakdown} dataKey="revenue" nameKey="trayType" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                    {data.trayTypeBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-muted-foreground py-8">No data</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── GM View ─────────────────────────────────────────────────────

function GmView({ data }: { data: GmDashboard }) {
  const { kpis } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <KpiCard title={kpis.accounting ? "Recognized Revenue (Posted GL)" : "Invoiced Revenue"} value={kpis.accounting?.recognizedRevenue ?? kpis.totalRevenue.current} delta={kpis.totalRevenue} format="currency" />
        <KpiCard title="Order / POS Value (Not Sales)" value={kpis.grossSales} format="currency" />
        <KpiCard title={kpis.accounting ? "Cash Received (Posted GL)" : "Cash Collected"} value={kpis.accounting?.postedCashReceived ?? kpis.cashCollected} format="currency" />
        <KpiCard title={kpis.accounting ? "Posted A/R Control (GL)" : "Outstanding Receivables"} value={kpis.accounting?.postedAccountsReceivable ?? kpis.outstandingReceivables} format="currency" />
        <KpiCard title="Orders" value={kpis.ordersCount.current} delta={kpis.ordersCount} />
        <KpiCard title="Walk-in Share of Order Value" value={kpis.walkInRevenueShare} format="percent" />
        <Card>
          <CardHeader className="pb-2"><CardDescription className="text-xs">Aging Buckets</CardDescription></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="flex justify-between"><span>0–30 days</span><span className="font-medium">KES {kpis.aging.zeroToThirty.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>31–60 days</span><span className="font-medium">KES {kpis.aging.thirtyOneToSixty.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>60+ days</span><span className="font-medium text-red-600">KES {kpis.aging.sixtyPlus.toLocaleString()}</span></div>
          </CardContent>
        </Card>
      </div>

      <AccountingExceptions accounting={kpis.accounting} />

      <Card>
        <CardHeader className="pb-2"><CardDescription>Pending Fulfillments by Stage</CardDescription></CardHeader>
        <CardContent className="flex gap-4 flex-wrap">
          <Badge variant="outline">Sowing: {kpis.pendingFulfillmentsByStage.sowing}</Badge>
          <Badge variant="outline">Germinating: {kpis.pendingFulfillmentsByStage.germinating}</Badge>
          <Badge variant="outline">Growing: {kpis.pendingFulfillmentsByStage.growing}</Badge>
          <Badge variant="outline">Ready for Dispatch: {kpis.pendingFulfillmentsByStage.readyForDispatch}</Badge>
        </CardContent>
      </Card>

      {/* Charts row */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Invoiced Sales by Month (Last 12)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.revenueByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Order Value by Tray Type</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.trayTypeBreakdown} dataKey="revenue" nameKey="trayType" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                  {data.trayTypeBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Company Seedling vs Propagation</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.seedlingTypeBreakdown} dataKey="revenue" nameKey="type" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                  {data.seedlingTypeBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Top 10 Customers by Order Value</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.topCustomersBar} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="customerName" width={120} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `KES ${Number(v).toLocaleString()}`} />
                <Bar dataKey="totalSpend" fill="#2563eb" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── POS View ────────────────────────────────────────────────────

function PosStockTable() {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const stock = usePosStock(pageNumber, pageSize);
  const page = stock.data;

  // A failed fetch cannot tell us whether this page still exists. Recover at page one.
  useEffect(() => {
    if (!stock.isError || pageNumber === 1) return;
    const timer = setTimeout(() => setPageNumber(1), 0);
    return () => clearTimeout(timer);
  }, [stock.isError, pageNumber]);

  // Live availability can shrink between pages. Return to a valid page after refresh.
  useEffect(() => {
    if (!page || stock.isPlaceholderData || pageNumber <= Math.max(1, page.totalPages)) return;
    const timer = setTimeout(() => setPageNumber(Math.max(1, page.totalPages)), 0);
    return () => clearTimeout(timer);
  }, [page, pageNumber, stock.isPlaceholderData]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Available Now</CardTitle>
        <Button variant="ghost" size="icon" aria-label="Refresh available stock"
          disabled={stock.isFetching} onClick={() => { void stock.refetch(); }}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent aria-busy={stock.isFetching}>
        {stock.isPending && <Skeleton className="h-40" />}
        {stock.error && (
          <div className="flex items-center justify-between gap-2 py-3 text-sm text-muted-foreground" role="alert">
            Stock availability could not be refreshed.
            <Button variant="outline" disabled={stock.isFetching} onClick={() => { void stock.refetch(); }}>Retry</Button>
          </div>
        )}
        {page && <>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Variety</TableHead>
              <TableHead>Tray Type</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Available</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {page.items.map((row) => (
                <TableRow key={row.entryId}>
                  <TableCell className="font-medium">{row.variety ?? row.cropType}</TableCell>
                  <TableCell>{row.trayType ?? "—"}</TableCell>
                  <TableCell>{row.stockUnit}</TableCell>
                  <TableCell className="text-right">{row.availableQuantity.toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {page.items.length === 0 && <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">No stock available</TableCell>
              </TableRow>}
            </TableBody>
          </Table>
          <PaginationBar currentPage={page.currentPage} pageSize={page.pageSize}
            totalCount={page.totalCount} totalPages={Math.max(1, page.totalPages)}
            onPageChange={(number) => { if (!stock.isPlaceholderData) setPageNumber(number); }}
            onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }} />
        </>}
      </CardContent>
    </Card>
  );
}

function PosView({ data }: { data: PosDashboard }) {
  const { kpis } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard title="Walk-ins Today" value={kpis.walkInOrdersToday} />
        <KpiCard title="Walk-in Order Value Today" value={kpis.walkInRevenueToday} format="currency" />
        <KpiCard title="Top Variety" value={kpis.topVarietyToday ?? "—"} />
        <KpiCard title="Top Variety Qty" value={kpis.topVarietyQuantityToday} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Today's Walk-Ins</CardTitle></CardHeader>
          <CardContent>
            <OrdersTable orders={data.todaysWalkIns} />
          </CardContent>
        </Card>

        <PosStockTable />
      </div>
    </div>
  );
}

// ── Agronomist Placeholder ──────────────────────────────────────

function AgronomistView({ message }: { message: string }) {
  return (
    <Card className="max-w-lg mx-auto mt-12">
      <CardHeader><CardTitle>Agronomist Dashboard</CardTitle></CardHeader>
      <CardContent><p className="text-muted-foreground">{message}</p></CardContent>
    </Card>
  );
}

function RegionalSalesSection({
  report,
  filters,
  onFiltersChange,
}: {
  report: RegionalSalesReport;
  filters: SalesSummaryFilters;
  onFiltersChange: (next: SalesSummaryFilters) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const totalPages = Math.max(1, Math.ceil(report.totalCount / report.pageSize));
  const setSort = (sortBy: SalesSummaryFilters["sortBy"]) => {
    const same = filters.sortBy === sortBy;
    onFiltersChange({ ...filters, sortBy, sortDescending: same ? !filters.sortDescending : true, pageNumber: 1 });
  };
  const sortMarker = (key: SalesSummaryFilters["sortBy"]) => filters.sortBy === key
    ? (filters.sortDescending ? " ↓" : " ↑")
    : "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Regional Sales</CardTitle>
        <CardDescription>
          Recognized invoice sales by order-origin region. Quantities remain separated into trays and pieces.
        </CardDescription>
        <div className="grid gap-2 pt-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={filters.regionId ?? "all"}
            onValueChange={(value) => onFiltersChange({ ...filters, regionId: value === "all" ? undefined : value, pageNumber: 1 })}
          >
            <SelectTrigger><SelectValue placeholder="All Regions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {report.regions.map((region) => <SelectItem key={region.id} value={region.id}>{region.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.cropId ?? "all"}
            onValueChange={(value) => onFiltersChange({
              ...filters,
              cropId: value === "all" ? undefined : value,
              varietyId: undefined,
              pageNumber: 1,
            })}
          >
            <SelectTrigger><SelectValue placeholder="All Crops" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Crops</SelectItem>
              {report.crops.map((crop) => <SelectItem key={crop.id} value={crop.id}>{crop.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.varietyId ?? "all"}
            onValueChange={(value) => onFiltersChange({ ...filters, varietyId: value === "all" ? undefined : value, pageNumber: 1 })}
            disabled={!filters.cropId}
          >
            <SelectTrigger><SelectValue placeholder={filters.cropId ? "All Varieties" : "Select Crop first"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Varieties</SelectItem>
              {report.varieties.map((variety) => <SelectItem key={variety.id} value={variety.id}>{variety.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            value={filters.search ?? ""}
            onChange={(event) => onFiltersChange({ ...filters, search: event.target.value || undefined, pageNumber: 1 })}
            placeholder="Search regions"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Filtered invoiced sales</div><div className="font-semibold">KES {report.totalInvoicedSales.toLocaleString()}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Trays</div><div className="font-semibold">{report.totalTrayQuantity.toLocaleString()}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Pieces / seedlings</div><div className="font-semibold">{report.totalPieceQuantity.toLocaleString()}</div></div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead><button type="button" onClick={() => setSort("region")}>Region{sortMarker("region")}</button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => setSort("sales")}>Invoiced Sales{sortMarker("sales")}</button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => setSort("quantity")}>Trays{sortMarker("quantity")}</button></TableHead>
              <TableHead className="text-right">Pieces</TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => setSort("orders")}>Orders{sortMarker("orders")}</button></TableHead>
              <TableHead className="text-right">Customers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.items.map((row) => {
              const key = row.regionId ?? "unassigned";
              const isOpen = expanded.has(key);
              return [
                <TableRow key={key}>
                  <TableCell>
                    <Button variant="ghost" size="icon" aria-label={`${isOpen ? "Collapse" : "Expand"} ${row.regionName}`} onClick={() => {
                      const next = new Set(expanded);
                      if (isOpen) next.delete(key); else next.add(key);
                      setExpanded(next);
                    }}>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium">{row.regionName}</TableCell>
                  <TableCell className="text-right">KES {row.invoicedSales.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.trayQuantity.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.pieceQuantity.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.orderCount.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.customerCount.toLocaleString()}</TableCell>
                </TableRow>,
                isOpen && (
                  <TableRow key={`${key}-products`}>
                    <TableCell />
                    <TableCell colSpan={6} className="bg-muted/30 p-4">
                      <Table>
                        <TableHeader><TableRow><TableHead>Crop</TableHead><TableHead>Variety</TableHead><TableHead>Unit / tray type</TableHead><TableHead className="text-right">Quantity</TableHead><TableHead className="text-right">Invoice-line sales</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {row.products.map((product, index) => <TableRow key={`${product.cropId}-${product.varietyId ?? "standard"}-${product.unit}-${product.trayType ?? "none"}-${index}`}>
                            <TableCell>{product.cropName}</TableCell>
                            <TableCell>{product.varietyName}</TableCell>
                            <TableCell>{product.unit}{product.trayType ? ` · ${product.trayType}` : ""}</TableCell>
                            <TableCell className="text-right">{product.quantity.toLocaleString()}</TableCell>
                            <TableCell className="text-right">KES {product.invoicedSales.toLocaleString()}</TableCell>
                          </TableRow>)}
                          {row.products.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No linked product lines</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                ),
              ];
            })}
            {report.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No regional sales match these filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {report.pageNumber} of {totalPages} · {report.totalCount} regions</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={report.pageNumber <= 1} onClick={() => onFiltersChange({ ...filters, pageNumber: report.pageNumber - 1 })}>Previous</Button>
            <Button variant="outline" size="sm" disabled={report.pageNumber >= totalPages} onClick={() => onFiltersChange({ ...filters, pageNumber: report.pageNumber + 1 })}>Next</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BusinessUnitView({ data, filters, onFiltersChange, accountingSection,
  summaryLoading, regionsLoading, itemsLoading, summaryError, regionsError, itemsError,
  retrySummary, retryRegions, retryItems,
}: {
  data: BusinessUnitSalesDashboard;
  filters: SalesSummaryFilters;
  onFiltersChange: (filters: SalesSummaryFilters) => void;
  accountingSection?: ReactNode;
  summaryLoading: boolean;
  regionsLoading: boolean;
  itemsLoading: boolean;
  summaryError?: string;
  regionsError?: string;
  itemsError?: string;
  retrySummary: () => void;
  retryRegions: () => void;
  retryItems: () => void;
}) {
  const label = data.businessUnit === "all" ? "All Sales"
    : data.businessUnit === "greenhouse" ? "Greenhouse" : "Agroforestry";
  const salesTable = (title: string, rows: Array<{ name: string; sales: number }>) => (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{title === "Sales by Region" ? "Region" : "Item"}</TableHead><TableHead className="text-right">Net invoiced sales</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((row) => <TableRow key={row.name}><TableCell>{row.name}</TableCell><TableCell className="text-right">KES {row.sales.toLocaleString()}</TableCell></TableRow>)}
            {rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground">No sales for this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
  const sectionState = (title: string, loading: boolean, error: string | undefined,
    retry: () => void, content: ReactNode) => {
    if (loading) return <Skeleton className="h-48" />;
    if (error) return <Card><CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3"><p className="break-words text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={retry}>Retry {title.toLowerCase()}</Button>
      </CardContent></Card>;
    return content;
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{label} · Net invoiced sales include issued invoice adjustments and linked credit notes.</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Select value={filters.regionId ?? "all"} onValueChange={(value) => onFiltersChange({ ...filters, regionId: value === "all" ? undefined : value, pageNumber: 1 })}>
          <SelectTrigger aria-label="Region"><SelectValue placeholder="All regions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {data.regions.map((region) => (
              <SelectItem key={region.id ?? "unassigned"} value={region.id ?? "unassigned"}>{region.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.cropId ?? "all"} onValueChange={(value) => onFiltersChange({
          ...filters, cropId: value === "all" ? undefined : value, varietyId: undefined, pageNumber: 1,
        })}>
          <SelectTrigger aria-label="Crop"><SelectValue placeholder="All crops" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All crops</SelectItem>
            {data.crops.map((crop) => <SelectItem key={crop.id} value={crop.id}>{crop.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.varietyId ?? "all"} disabled={!filters.cropId}
          onValueChange={(value) => onFiltersChange({ ...filters, varietyId: value === "all" ? undefined : value, pageNumber: 1 })}>
          <SelectTrigger aria-label="Variety"><SelectValue placeholder="All varieties" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All varieties</SelectItem>
            {data.varieties.map((variety) => <SelectItem key={variety.id} value={variety.id}>{variety.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={filters.search ?? ""} onChange={(event) => onFiltersChange({
          ...filters, search: event.target.value || undefined, pageNumber: 1,
        })} placeholder="Search regions" aria-label="Search regions" />
      </div>
      {sectionState("Sales summary", summaryLoading, summaryError, retrySummary, <div className="grid gap-4 sm:grid-cols-2">
        <KpiCard title="Net invoiced sales" value={data.totalSales} format="currency" />
        <KpiCard title="Total orders" value={data.totalOrders} />
      </div>)}
      {accountingSection}
      <div className="grid gap-4 xl:grid-cols-2">
        {sectionState("Sales by Region", regionsLoading, regionsError, retryRegions,
          salesTable("Sales by Region", data.regions))}
        <div className="space-y-2">
          {sectionState("Sales by Item", itemsLoading, itemsError, retryItems,
            salesTable("Sales by Item", data.items))}
          {!itemsLoading && !itemsError && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Page {data.itemPageNumber} of {Math.max(1, Math.ceil(data.itemTotalCount / data.itemPageSize))} · {data.itemTotalCount} items</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={data.itemPageNumber <= 1}
                onClick={() => onFiltersChange({ ...filters, pageNumber: data.itemPageNumber - 1 })}>Previous</Button>
              <Button variant="outline" size="sm" disabled={data.itemPageNumber * data.itemPageSize >= data.itemTotalCount}
                onClick={() => onFiltersChange({ ...filters, pageNumber: data.itemPageNumber + 1 })}>Next</Button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────

const ROLE_TITLES: Record<string, string> = {
  BusinessUnit: "Sales Summary",
  SalesRep: "My Sales Summary",
  SalesManager: "Sales Team Summary",
  GM: "Leadership Sales Overview",
  POS: "Front Office — Today",
  Agronomist: "Agronomist Dashboard",
};

const ROLE_DEFAULT_SCOPES: Record<string, Scope> = {
  BusinessUnit: "month",
  SalesRep: "week",
  SalesManager: "month",
  GM: "month",
  POS: "today",
  Agronomist: "month",
};

export default function SalesSummaryPage() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [scope, setScope] = useState<Scope>("month");
  const [specificDate, setSpecificDate] = useState<string>(() => getBusinessDateToday("Africa/Nairobi"));
  const [customRange, setCustomRange] = useState<{ from?: string; to?: string }>({
    from: getBusinessDateToday("Africa/Nairobi").slice(0, 7) + "-01",
    to: getBusinessDateToday("Africa/Nairobi"),
  });
  const dates = scopeDates(scope, specificDate, customRange);
  const [regionalFilters, setRegionalFilters] = useState<SalesSummaryFilters>({
    businessUnit: "auto",
    sortBy: "sales",
    sortDescending: true,
    pageNumber: 1,
    pageSize: 10,
  });
  const debouncedSearch = useDebounce(regionalFilters.search, 300);
  const requestFilters = { ...regionalFilters, search: regionalFilters.search ? debouncedSearch : undefined };
  const canViewAllBusinessUnits = useAuthStore((state) => state.permissions.includes("sales.view_all_business_units"));
  const canViewAgroforestry = useAuthStore((state) => state.permissions.includes("sales.view_agroforestry_summary"));
  const isBusinessUnitView = canViewAllBusinessUnits || canViewAgroforestry;
  const { data, isLoading, error, refetch } = useSalesSummary(
    scope, dates.from, dates.to, requestFilters, !isBusinessUnitView);
  const summaryQuery = useBusinessUnitSalesSection("summary", scope, dates.from, dates.to,
    { ...requestFilters, pageNumber: 1 }, isBusinessUnitView);
  const regionsQuery = useBusinessUnitSalesSection("regions", scope, dates.from, dates.to,
    { ...requestFilters, pageNumber: 1 }, isBusinessUnitView);
  const itemsQuery = useBusinessUnitSalesSection("items", scope, dates.from, dates.to,
    requestFilters, isBusinessUnitView);
  const showIndependentAccounting = canViewAllBusinessUnits
    && (regionalFilters.businessUnit === "all" || regionalFilters.businessUnit === "auto")
    && !regionalFilters.regionId && !regionalFilters.cropId
    && !regionalFilters.varietyId && !regionalFilters.search;
  const accountingQuery = useSalesSummaryAccounting(
    scope, dates.from, dates.to, showIndependentAccounting);
  const accountingSection = showIndependentAccounting
    ? <IndependentAccountingSection
        accounting={accountingQuery.data}
        loading={accountingQuery.isPending}
        failed={Boolean(accountingQuery.error)}
        retry={() => accountingQuery.refetch()}
      />
    : null;

  const exportPdf = async () => {
    setExporting(true);
    setExportError(undefined);
    try {
      await exportSalesSummaryPdf(scope, dates.from, dates.to, regionalFilters);
    } catch (error) {
      setExportError(normalizeError(error).userMessage);
    } finally {
      setExporting(false);
    }
  };

  const selectBusinessUnit = (businessUnit: NonNullable<SalesSummaryFilters["businessUnit"]>) =>
    setRegionalFilters((filters) => ({
      ...filters, businessUnit, regionId: undefined, cropId: undefined,
      varietyId: undefined, search: undefined, pageNumber: 1,
    }));

  // Once role is known, set scope to role default (only on first load)
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!data || initialized) return;
    const defaultScope = ROLE_DEFAULT_SCOPES[data.role] ?? "month";
    const timer = setTimeout(() => {
      setScope(defaultScope);
      setInitialized(true);
    }, 0);
    return () => clearTimeout(timer);
  }, [data, initialized]);

  // Auto-refresh for POS (every 60s)
  useEffect(() => {
    if (data?.role !== "POS") return;
    const interval = setInterval(() => refetch(), 60_000);
    return () => clearInterval(interval);
  }, [data?.role, refetch]);

  if (isBusinessUnitView) {
    const selectedUnit = regionalFilters.businessUnit === "auto"
      ? canViewAllBusinessUnits ? "all" : "agroforestry"
      : regionalFilters.businessUnit ?? "all";
    const allowedBusinessUnits: BusinessUnitSalesDashboard["allowedBusinessUnits"] = canViewAllBusinessUnits
      ? ["all", "greenhouse", "agroforestry"] : ["agroforestry"];
    const fallback: BusinessUnitSalesDashboard = {
      role: "BusinessUnit", scope: { from: dates.from ?? "", to: dates.to ?? "" },
      businessUnit: selectedUnit, allowedBusinessUnits, totalSales: 0, totalOrders: 0,
      regions: [], items: [], itemTotalCount: 0, itemPageNumber: 1,
      itemPageSize: regionalFilters.pageSize ?? 10, crops: [], varieties: [],
      unclassifiedLineCount: 0, unclassifiedAmount: 0,
    };
    const report: BusinessUnitSalesDashboard = {
      ...fallback, ...summaryQuery.data,
      regions: regionsQuery.data?.regions ?? [],
      items: itemsQuery.data?.items ?? [],
      itemTotalCount: itemsQuery.data?.itemTotalCount ?? 0,
      itemPageNumber: itemsQuery.data?.itemPageNumber ?? regionalFilters.pageNumber ?? 1,
      itemPageSize: itemsQuery.data?.itemPageSize ?? regionalFilters.pageSize ?? 10,
    };
    const displayedScope = summaryQuery.data?.scope ?? regionsQuery.data?.scope ?? itemsQuery.data?.scope;
    const refresh = () => {
      void summaryQuery.refetch();
      void regionsQuery.refetch();
      void itemsQuery.refetch();
      if (showIndependentAccounting) void accountingQuery.refetch();
    };
    return <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sales Summary</h1>
          {(displayedScope?.from ?? dates.from) && (displayedScope?.to ?? dates.to) &&
            <p className="text-sm text-muted-foreground">{displayedScope?.from ?? dates.from} — {displayedScope?.to ?? dates.to}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScopeSelector scope={scope} onChange={setScope} specificDate={specificDate} />
          {scope === "specific" && <Input type="date" value={specificDate} onChange={(event) => setSpecificDate(event.target.value)} className="w-auto" />}
          {scope === "custom" && <DateRangePicker startDate={customRange.from} endDate={customRange.to}
            onChange={(range) => setCustomRange({ from: range.startDate, to: range.endDate })}
            placeholder="Select reporting range" className="w-[300px]" />}
          <Button variant="outline" onClick={exportPdf} disabled={exporting || summaryQuery.isPending || !!summaryQuery.error}>
            <Download className="h-4 w-4" />{exporting ? "Exporting…" : "Export PDF"}
          </Button>
          <Button variant="ghost" size="icon" aria-label="Refresh sales summary" onClick={refresh}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      {exportError && <p role="alert" className="text-sm text-destructive">PDF export failed: {exportError}</p>}
      {allowedBusinessUnits.length > 1 && <Tabs value={selectedUnit}
        onValueChange={(value) => selectBusinessUnit(value as NonNullable<SalesSummaryFilters["businessUnit"]>)}>
        <TabsList aria-label="Business Unit" className="h-auto w-full flex-wrap justify-start sm:w-fit">
          {allowedBusinessUnits.map((unit) => <TabsTrigger key={unit} value={unit}>
            {unit === "all" ? "All Sales" : unit === "greenhouse" ? "Greenhouse" : "Agroforestry"}
          </TabsTrigger>)}
        </TabsList>
      </Tabs>}
      <BusinessUnitView data={report} filters={regionalFilters} onFiltersChange={setRegionalFilters}
        accountingSection={accountingSection}
        summaryLoading={summaryQuery.isPending} regionsLoading={regionsQuery.isPending} itemsLoading={itemsQuery.isPending}
        summaryError={summaryQuery.error ? normalizeError(summaryQuery.error).userMessage : undefined}
        regionsError={regionsQuery.error
          ? normalizeError(regionsQuery.error).httpStatus === 409
            ? "Unknown business-unit sales must be reconciled before regional totals can be shown."
            : normalizeError(regionsQuery.error).userMessage : undefined}
        itemsError={itemsQuery.error
          ? normalizeError(itemsQuery.error).httpStatus === 409
            ? "Unknown business-unit sales must be reconciled before item totals can be shown."
            : normalizeError(itemsQuery.error).userMessage : undefined}
        retrySummary={() => { void summaryQuery.refetch(); }}
        retryRegions={() => { void regionsQuery.refetch(); }}
        retryItems={() => { void itemsQuery.refetch(); }} />
    </div>;
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        {accountingQuery.data && accountingSection}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <ScopeSelector scope={scope} onChange={setScope} showYtd={false} specificDate={specificDate} />
          {scope === "specific" && (
            <Input type="date" value={specificDate} onChange={(event) => setSpecificDate(event.target.value)} className="w-auto" />
          )}
          {scope === "custom" && (
            <DateRangePicker startDate={customRange.from} endDate={customRange.to}
              onChange={(range) => setCustomRange({ from: range.startDate, to: range.endDate })}
              placeholder="Select reporting range" className="w-[300px]" />
          )}
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-4">{error ? normalizeError(error).userMessage : "Failed to load dashboard"}</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => refetch()}>Retry</Button>
              <Button variant="outline" onClick={() => setRegionalFilters({
                businessUnit: "auto", pageNumber: 1, pageSize: 10, sortBy: "sales", sortDescending: true,
              })}>Clear filters</Button>
            </div>
          </CardContent>
        </Card>
        {accountingSection}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{ROLE_TITLES[data.role] ?? "Sales Summary"}</h1>
          {"scope" in data && <p className="text-sm text-muted-foreground">{data.scope.from} — {data.scope.to}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.role === "BusinessUnit" && data.allowedBusinessUnits.length > 1 && (
            <Select value={data.businessUnit} onValueChange={(value) => selectBusinessUnit(value as NonNullable<SalesSummaryFilters["businessUnit"]>)}>
              <SelectTrigger className="w-full sm:w-[170px]" aria-label="Business Unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.allowedBusinessUnits.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit === "all" ? "All Sales" : unit === "greenhouse" ? "Greenhouse" : "Agroforestry"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {data.role !== "POS" && (
            <>
              <ScopeSelector
                scope={scope}
                onChange={setScope}
                showYtd={data.role === "GM"}
                specificDate={specificDate}
              />
              {scope === "specific" && (
                <Input
                  type="date"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                />
              )}
              {scope === "custom" && (
                <DateRangePicker
                  startDate={customRange.from}
                  endDate={customRange.to}
                  onChange={(range) => setCustomRange({ from: range.startDate, to: range.endDate })}
                  placeholder="Select reporting range"
                  className="w-[300px]"
                />
              )}
            </>
          )}
          <Button variant="ghost" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {data.role === "SalesRep" && <SalesRepView data={data} />}
      {data.role === "SalesManager" && <SalesManagerView data={data} />}
      {data.role === "GM" && <GmView data={data} />}
      {data.role === "POS" && <PosView data={data} />}
      {data.role === "Agronomist" && <AgronomistView message={data.message} />}
      {(data.role === "GM" || data.role === "SalesManager") && data.regionalSales && (
        <RegionalSalesSection report={data.regionalSales} filters={regionalFilters} onFiltersChange={setRegionalFilters} />
      )}
    </div>
  );
}
